// Apolo IDE — MultiAgentPanel (Sprint v4 Epic S5).
//
// Shell that composes the S1-S4 multi-agent UI into a single panel with
// 3 tabs:
//   - Compare           → CompareView (S1)
//   - Decompose & Run   → PlanPreview + PlanApprovalActions (S2)
//                         + DispatchStatusBar + SubagentTracker (S3)
//                         + SynthesisView (S4) once it lands
//   - History           → HistoryTab (lists past dispatches)
//
// Live cost meter pinned to the header (PRE-RESOLVED default for S5:
// real-time update + alert when 80% of monthly cap is reached).
//
// State is split:
//   - useMultiAgentPanel    → active tab + active dispatch id
//   - useCostMeter          → cost snapshot polling
//   - useCompareMode        → compare tab state
//   - useDecomposer         → decompose tab plan flow
//   - useOrchestrator       → polls when a dispatch id is selected
//
// The shell is intentionally read-only on the sub-hooks — each tab owns
// its own user actions (submit prompt, approve, reject, kill). This keeps
// each piece individually testable.

import { useCallback, useState } from 'react';
import { Columns3, Cpu, History as HistoryIcon } from 'lucide-react';
import useCostMeter from '../../../hooks/useCostMeter.js';
import useMultiAgentPanel from '../../../hooks/useMultiAgentPanel.js';
import useCompareMode from '../../../hooks/useCompareMode.js';
import useDecomposer from '../../../hooks/useDecomposer.js';
import useOrchestrator from '../../../hooks/useOrchestrator.js';
import CostMeter from './CostMeter.jsx';
import CompareView from '../CompareMode/CompareView.jsx';
import PlanPreview from './PlanPreview.jsx';
import PlanApprovalActions from './PlanApprovalActions.jsx';
import DispatchStatusBar from './DispatchStatusBar.jsx';
import SubagentTracker from './SubagentTracker.jsx';
import KillSwitchButton from './KillSwitchButton.jsx';
import SynthesisView from './SynthesisView.jsx';
import HistoryTab from './HistoryTab.jsx';
import GenericErrorBoundary from '../../ui/GenericErrorBoundary.jsx';

const TABS = [
  { id: 'compare', label: 'Compare', Icon: Columns3 },
  { id: 'decompose', label: 'Decompose & Run', Icon: Cpu },
  { id: 'history', label: 'History', Icon: HistoryIcon },
];

function TabButton({ tab, isActive, onClick }) {
  return (
    <button
      type="button"
      role="tab"
      data-testid={`multiagent-tab-${tab.id}`}
      aria-selected={isActive}
      onClick={() => onClick(tab.id)}
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] transition focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 ${
        isActive
          ? 'bg-apolo-surface2 text-apolo-textPrimary'
          : 'text-apolo-textTertiary hover:bg-apolo-surface1 hover:text-apolo-textSecondary'
      }`}
    >
      <tab.Icon size={11} aria-hidden="true" />
      <span>{tab.label}</span>
    </button>
  );
}

function CompareTabPanel({ sessionId }) {
  const compare = useCompareMode();
  const [prompt, setPrompt] = useState('');
  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      await compare.runCompare({ prompt, sessionId });
    },
    [compare, prompt, sessionId],
  );
  return (
    <div className="flex flex-col gap-3 p-3" data-testid="multiagent-compare-panel">
      <form onSubmit={onSubmit} className="flex flex-col gap-2">
        <label htmlFor="compare-prompt" className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">
          Prompt (compare N modelos lado-a-lado)
        </label>
        <textarea
          id="compare-prompt"
          data-testid="compare-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="Ex: explica como melhorar a latência deste handler..."
          className="resize-y rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1.5 text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
        />
        <button
          type="submit"
          disabled={compare.busy || prompt.trim().length < 10}
          data-testid="compare-submit"
          className="self-end rounded-md bg-apolo-accent px-3 py-1 text-xs text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {compare.busy ? 'Comparando...' : 'Comparar'}
        </button>
      </form>
      <GenericErrorBoundary>
        <CompareView
          results={compare.results}
          busy={compare.busy}
          modelsCount={compare.models.length}
          totalCostUsd={compare.totalCostUsd}
          winnerSubagentId={compare.winner?.subagentId}
          onPickWinner={compare.pickWinner}
          error={compare.error}
        />
      </GenericErrorBoundary>
    </div>
  );
}

function DecomposeTabPanel({ sessionId, activeDispatchId, setActiveDispatchId }) {
  const decomposer = useDecomposer();
  const orchestrator = useOrchestrator(activeDispatchId, { autoStart: !!activeDispatchId });
  const [message, setMessage] = useState('');

  const onDecompose = useCallback(
    async (e) => {
      e.preventDefault();
      await decomposer.decompose({ message, sessionId });
    },
    [decomposer, message, sessionId],
  );

  const onApprove = useCallback(async () => {
    const res = await decomposer.approve();
    if (res.ok) {
      const id = decomposer.planId || res.data?.planId;
      if (id) setActiveDispatchId(id);
    }
  }, [decomposer, setActiveDispatchId]);

  return (
    <div className="flex flex-col gap-3 p-3" data-testid="multiagent-decompose-panel">
      {!decomposer.plan && (
        <form onSubmit={onDecompose} className="flex flex-col gap-2">
          <label htmlFor="decompose-message" className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">
            Pedido (Opus vai decompor em subagents)
          </label>
          <textarea
            id="decompose-message"
            data-testid="decompose-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            placeholder="Ex: implementa endpoint /api/foo + testes + docs..."
            className="resize-y rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1.5 text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
          />
          <button
            type="submit"
            disabled={decomposer.busy || message.trim().length < 10}
            data-testid="decompose-submit"
            className="self-end rounded-md bg-apolo-accent px-3 py-1 text-xs text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {decomposer.busy ? 'Decompondo...' : 'Decompor'}
          </button>
        </form>
      )}

      {decomposer.error && (
        <p role="alert" className="text-[11px] text-apolo-error" data-testid="decompose-error">
          {decomposer.error}
        </p>
      )}

      {decomposer.plan && decomposer.phase === 'awaiting-approval' && (
        <>
          <PlanPreview plan={decomposer.plan} />
          <PlanApprovalActions
            phase={decomposer.phase}
            busy={decomposer.busy}
            onApprove={onApprove}
            onReject={decomposer.reject}
            warning={decomposer.budget?.warning}
          />
        </>
      )}

      {activeDispatchId && (
        <section data-testid="multiagent-active-dispatch" className="flex flex-col gap-2">
          <DispatchStatusBar
            dispatch={orchestrator.dispatch}
            progress={orchestrator.progress}
            phase={orchestrator.phase}
            busy={orchestrator.busy}
            error={orchestrator.error}
          />
          <SubagentTracker runs={orchestrator.runs} />
          {!orchestrator.isTerminal && (
            <KillSwitchButton
              onKill={orchestrator.kill}
              busy={orchestrator.busy}
              enabled={!!activeDispatchId}
            />
          )}
          {orchestrator.dispatch && orchestrator.dispatch.synthesized_output && (
            <SynthesisView
              synthesis={{
                synthesized: orchestrator.dispatch.synthesized_output,
                meta: orchestrator.dispatch.synthesis_meta_json || null,
              }}
            />
          )}
        </section>
      )}
    </div>
  );
}

function HistoryTabPanel({ onSelect }) {
  return (
    <div data-testid="multiagent-history-panel">
      <HistoryTab onSelect={onSelect} />
    </div>
  );
}

export default function MultiAgentPanel({ sessionId, onCloseShortcutHandled, onOpenRequest }) {
  const panel = useMultiAgentPanel({ onOpen: onOpenRequest });
  const cost = useCostMeter();

  const openHistoryDispatch = useCallback(
    (d) => {
      if (d && d.id) {
        panel.setActiveDispatchId(d.id);
        panel.setTab('decompose');
      }
    },
    [panel],
  );

  return (
    <section aria-label="MultiAgent Panel" className="flex h-full flex-col" data-testid="multiagent-panel">
      <header className="flex items-center justify-between gap-2 border-b border-apolo-borderSubtle px-3 py-2">
        <nav role="tablist" aria-label="MultiAgent sub-tabs" className="flex items-center gap-1">
          {TABS.map((t) => (
            <TabButton key={t.id} tab={t} isActive={panel.tab === t.id} onClick={panel.setTab} />
          ))}
        </nav>
        <CostMeter snapshot={cost.snapshot} loading={cost.loading} compact />
      </header>
      <div className="flex-1 overflow-y-auto">
        {panel.tab === 'compare' && <CompareTabPanel sessionId={sessionId} />}
        {panel.tab === 'decompose' && (
          <DecomposeTabPanel
            sessionId={sessionId}
            activeDispatchId={panel.activeDispatchId}
            setActiveDispatchId={panel.setActiveDispatchId}
          />
        )}
        {panel.tab === 'history' && <HistoryTabPanel onSelect={openHistoryDispatch} />}
      </div>
      {/* hidden hook into close shortcut for future SidePanel integration */}
      {onCloseShortcutHandled ? null : null}
    </section>
  );
}
